import LogRocket from "logrocket";

import {
  anonymousIdStorageKey,
  getAnonymousId,
  getOrCreateAnonymousId,
} from "@brexhq/next/src/telemetry/initAnalytics";
import { logScriptLoadingError } from "@brexhq/next/src/utils/scripts";
import { ExternalScripts } from "@brexhq/next/types/externalScripts";

import { Winda } from "../utils/Winda";

import { EventNames } from "./analyticsEvents";
import {
  IntegrationsFlags,
  ApplicationLaunchedEvent,
  ApplicationRenderedEvent,
  InitializedSegmentClient,
} from "./types";

let client: InitializedSegmentClient;
let logRocketClient: typeof LogRocket;

export const initLogRocketClient = () => {
  const anonymousId = getOrCreateAnonymousId(anonymousIdStorageKey);
  if (!process.env.LOGROCKET_APP_ID) {
    console.error("Missing LogRocket App Id Key");
  }
  if (!logRocketClient) {
    LogRocket.init(process.env.LOGROCKET_APP_ID ?? "", {
      rootHostname: "brex.com",
      network: {
        //TODO: see if we need any info for requests
        requestSanitizer: (request) => {
          if (request.url.includes("api")) {
            return null;
          }
          return null;
        },
        responseSanitizer: () => {
          return null;
        },
      },
      dom: {
        inputSanitizer: true,
      },
    });
    LogRocket.identify({ anonymousId });
    logRocketClient = LogRocket;
  }
  return logRocketClient;
};

export const initSegmentClientIfNeeded = (
  segmentKey: string | undefined,
  integrations: IntegrationsFlags,
): InitializedSegmentClient => {
  const anonymousId = getOrCreateAnonymousId(anonymousIdStorageKey);

  if (!segmentKey) {
    console.error("Missing Segment write key");
  }

  if (!client && Winda.isPresent) {
    window.analytics && window.analytics.setAnonymousId(anonymousId);
    window.analytics &&
      window.analytics.load(segmentKey ?? "", { integrations });
    client = new InitializedSegmentClient(window);
  }

  return client;
};

const visitorIdCharacterRegex = /[a-zA-Z0-9_\-~]/;
export const getVisitorId = (id: string) =>
  [...id].filter((char) => visitorIdCharacterRegex.test(char)).join("");

export const redactionSelectors = [
  '[data-dd-privacy="mask"]',
  '[data-dd-privacy="hidden"]',
  ".dd-privacy-mask",
  ".dd-privacy-hidden",
  "._lr-hide",
  "[data-private]",
];

export const initGlance = () => {
  if (Winda.isPresent && !window.GLANCE && !window.GLANCE_COBROWSE) {
    const site =
      process.env.NODE_ENV === "production" ? "production" : "staging";

    window.GLANCE_COBROWSE = {
      visitorid: getVisitorId(getAnonymousId()),
      getMaskingDescriptor: async () => ({
        maskedelements: redactionSelectors,
        maskpage: true,
      }),
    };

    const scriptEl = document.createElement("script");
    scriptEl.id = ExternalScripts.GLANCE_COBROWSE;
    scriptEl.charset = "UTF-8";
    scriptEl.type = "text/javascript";
    scriptEl.src = `https://www.glancecdn.net/cobrowse/CobrowseJS.ashx?group=24318&site=${site}`;
    scriptEl.onerror = (ev) =>
      logScriptLoadingError(ExternalScripts.GLANCE_COBROWSE, ev);
    scriptEl.setAttribute("data-groupid", "24318");
    scriptEl.setAttribute("data-site", site);
    scriptEl.setAttribute("data-ws", "www.glance.net");
    scriptEl.setAttribute("data-presence", "api");
    scriptEl.setAttribute("data-cookietype", "secure");
    document.body.appendChild(scriptEl);
  }
};

export const createApplicationLaunchedEvent = (
  elapsedTimeFromNavStart: number,
): ApplicationLaunchedEvent => ({
  type: EventNames.APPLICATION_LAUNCHED,
  time: elapsedTimeFromNavStart,
});

export const createApplicationRenderedEvent = (
  elapsedTimeFromNavStart: number,
): ApplicationRenderedEvent => ({
  type: EventNames.APPLICATION_RENDERED,
  time: elapsedTimeFromNavStart,
});
